import { Action, combineReducers, Reducer, ReducersMapObject, Store } from '@reduxjs/toolkit';

// Define the PersistState interface manually since it's not exported
interface PersistState {
  version: number;
  rehydrated: boolean;
}

/**
 * A custom reducer manager that allows dynamically adding and removing reducers
 */
export interface ReducerManager {
  getReducerMap: () => ReducersMapObject;
  reduce: (state: any, action: Action) => any;
  add: (key: string, reducer: Reducer) => void;
  remove: (key: string) => void;
}

/**
 * Creates a reducer manager that allows dynamically adding and removing reducers
 */
export function createReducerManager(initialReducers: ReducersMapObject): ReducerManager {
  // Create an object which maps keys to reducers
  const reducers = { ...initialReducers };

  // Create the initial combinedReducer
  let combinedReducer = combineReducers(reducers);

  // An array which is used to delete state keys when reducers are removed
  let keysToRemove: string[] = [];

  return {
    getReducerMap: () => reducers,

    // The root reducer function exposed by this object
    // This will be passed to the store
    reduce: (state: any, action: Action) => {
      // If any reducers have been removed, clean up their state first
      if (keysToRemove.length > 0) {
        state = { ...state };
        for (let key of keysToRemove) {
          delete state[key];
        }
        keysToRemove = [];
      }

      // Delegate to the combined reducer
      return combinedReducer(state, action);
    },

    // Adds a new reducer with the specified key
    add: (key: string, reducer: Reducer) => {
      if (!key || reducers[key]) {
        console.log(`⚠️ Reducer ${key} already exists or key is invalid`);
        return false;
      }

      console.log(
        `📝 Adding reducer "${key}" to dynamic store. Current reducers:`,
        Object.keys(reducers),
      );

      // Add the reducer to the reducer mapping
      reducers[key] = reducer;

      // Generate a new combined reducer
      combinedReducer = combineReducers(reducers);

      console.log(`✅ Reducer "${key}" added. New reducers map:`, Object.keys(reducers));
      return true;
    },

    // Removes a reducer with the specified key
    remove: (key: string) => {
      if (!key || !reducers[key]) {
        return;
      }

      // Remove it from the reducer mapping
      delete reducers[key];

      // Add the key to the list of keys to clean up
      keysToRemove.push(key);

      // Generate a new combined reducer
      combinedReducer = combineReducers(reducers);
    },
  };
}

// Extended Store interface with reducerManager
// Array to track middleware for dynamically injected APIs
export type MiddlewareRegistry = {
  addMiddleware: (middleware: any) => void;
  removeMiddleware: (middleware: any) => void;
  getMiddleware: () => any[];
};

// Enhanced store with dynamic reducer support
export interface StoreWithReducerManager extends Store {
  reducerManager: ReducerManager;
  dynamicMiddleware?: any; // Redux Toolkit's dynamic middleware instance
}

// Helper function to check if a store has dynamic reducers
export function isDynamicStore(store: Store): store is StoreWithReducerManager {
  return 'reducerManager' in store;
}

/**
 * Create a middleware registry to manage dynamically added/removed middleware
 */
export function createMiddlewareRegistry(): MiddlewareRegistry {
  const middlewareList: any[] = [];

  return {
    addMiddleware: (middleware: any) => {
      if (!middlewareList.includes(middleware)) {
        middlewareList.push(middleware);
      }
    },
    removeMiddleware: (middleware: any) => {
      const index = middlewareList.indexOf(middleware);
      if (index !== -1) {
        middlewareList.splice(index, 1);
      }
    },
    getMiddleware: () => [...middlewareList],
  };
}

// Type for persisted state
export interface PersistedState {
  _persist: PersistState;
}
